import type { AxiosError, InternalAxiosRequestConfig } from "axios";
import type { ApiRequestConfig, ApiErrorResponseDto } from "@/src/types/api.types";
import { notifyApiError } from "@/src/api/apiErrorToast";
import { notifySubscriptionError } from "@/src/utils/subscriptionErrors";
import {
  ApiClientError,
  isDeactivatedAccountError,
  isTimeoutError,
  parseAxiosError,
} from "@/src/utils/apiError";

type UnauthorizedHandler = () => void;

export function rejectAxiosResponseError(
  error: AxiosError<ApiErrorResponseDto>,
  options?: { onUnauthorized?: UnauthorizedHandler },
): Promise<never> {
  const config = error.config as (InternalAxiosRequestConfig & ApiRequestConfig) | undefined;

  // Abort/cancel (e.g. debounced search) should be silent.
  if (error.code === "ERR_CANCELED") {
    return Promise.reject(error);
  }

  if (isTimeoutError(error)) {
    const clientError = new ApiClientError(
      "Request timed out. Check your connection and try again.",
      408,
    );
    maybeToastApiError(clientError, config);
    return Promise.reject(clientError);
  }

  const shouldForceLogout =
    config && !config.skipAuth && !config.suppressUnauthorizedRedirect;

  if (error.response?.status === 401 && shouldForceLogout) {
    options?.onUnauthorized?.();
    return Promise.reject(parseAxiosError(error));
  }

  if (
    isDeactivatedAccountError(error.response?.status, error.response?.data) &&
    shouldForceLogout
  ) {
    options?.onUnauthorized?.();
    return Promise.reject(parseAxiosError(error));
  }

  const clientError = error.response
    ? parseAxiosError(error)
    : new ApiClientError(error.message || "Network error. Please try again.", 0);

  maybeToastApiError(clientError, config);
  return Promise.reject(clientError);
}

function maybeToastApiError(
  error: ApiClientError,
  config?: InternalAxiosRequestConfig & ApiRequestConfig,
): void {
  if (config?.skipGlobalErrorHandler) {
    return;
  }
  if (notifySubscriptionError(error, { linkToPlans: true })) {
    return;
  }
  notifyApiError(error.message);
}
